/**
 * Step 2: Drug & Population
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Select, Input, Button, Divider, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { adminAPI } from '../../services/api';
import { loadAdminDrugOptions, type AdminDrugOption } from '../../utils/adminDrugOptions';
import { formatErrorMessage } from '../../utils/errorHandler';

const { TextArea } = Input;
const { Text } = Typography;

interface Step2Props {
  data: any;
  onChange: (data: any) => void;
  formRef?: React.MutableRefObject<any>;
}

const Step2DrugPopulation: React.FC<Step2Props> = ({ data, onChange, formRef }) => {
  const [form] = Form.useForm();
  const [drugOptions, setDrugOptions] = useState<AdminDrugOption[]>([]);
  const [drugOptionsLoading, setDrugOptionsLoading] = useState(false);
  const [populationTags, setPopulationTags] = useState<string[]>([]);
  const watchedDrugName = Form.useWatch('drug_name', form);

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      drug_name: data.drug_name === 'pending' ? '' : data.drug_name,
      inclusion_criteria: data.inclusion_criteria || [],
      exclusion_criteria: data.exclusion_criteria || [],
    });
    if (formRef) formRef.current = form;
  }, [data, form, formRef]);

  useEffect(() => {
    void loadDrugOptions();
    void loadPopulationTags();
  }, []);

  const loadDrugOptions = async () => {
    setDrugOptionsLoading(true);
    try {
      const options = await loadAdminDrugOptions();
      setDrugOptions(options);
    } catch (error: any) {
      message.error(formatErrorMessage(error, '加载药物列表失败'));
      console.error('Failed to load drug options:', error);
    } finally {
      setDrugOptionsLoading(false);
    }
  };

  const loadPopulationTags = async () => {
    try {
      const res = await adminAPI.getPopulationTags();
      setPopulationTags(res.tags || []);
    } catch (error: any) {
      message.error(formatErrorMessage(error, '加载人群标签失败'));
      console.error('Failed to load population tags:', error);
    }
  };

  const currentDrugValue = String(watchedDrugName || data?.drug_name || '').trim();

  const selectOptions = useMemo(() => {
    return (drugOptions || []).map((drug) => ({
      value: drug.value,
      label: drug.label,
    }));
  }, [drugOptions]);

  const hasLegacyDrugValue = Boolean(
    currentDrugValue &&
    currentDrugValue !== 'pending' &&
    !drugOptions.some((drug) => drug.value === currentDrugValue),
  );

  const handleValuesChange = (_: any, allValues: any) => {
    onChange(allValues);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
      initialValues={{
        ...data,
        drug_name: data.drug_name === 'pending' ? '' : data.drug_name,
        inclusion_criteria: data.inclusion_criteria || [],
        exclusion_criteria: data.exclusion_criteria || [],
      }}
    >
      <Form.Item
        label="药物名称"
        name="drug_name"
        rules={[
          { required: true, message: '请选择药物名称' },
          {
            validator: async (_, value) => {
              if (!value || drugOptions.some((drug) => drug.value === value)) return;
              throw new Error('请选择后台已配置的药物');
            },
          },
        ]}
        extra={hasLegacyDrugValue ? <Text type="warning">当前药物未在后台配置中，请重新选择。</Text> : '请选择已配置的药物'}
      >
        <Select
          showSearch
          allowClear
          loading={drugOptionsLoading}
          placeholder="请选择药物名称"
          options={selectOptions}
          notFoundContent={drugOptionsLoading ? 'Loading...' : 'No drugs available'}
          optionFilterProp="label"
          filterOption={(input, option) => {
            const label = String(option?.label || '').toLowerCase();
            const value = String(option?.value || '').toLowerCase();
            const keyword = input.toLowerCase();
            return label.includes(keyword) || value.includes(keyword);
          }}
        />
      </Form.Item>

      <Form.Item label="人群描述" name="population">
        <TextArea rows={3} placeholder="例如：新生儿重症监护（NICU）" />
      </Form.Item>

      <Form.Item label="人群标签" name="population_tags">
        <Select mode="multiple" placeholder="选择人群标签">
          {populationTags.map(tag => (
            <Select.Option key={tag} value={tag}>
              {tag}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="适应症" name="indication">
        <TextArea rows={2} placeholder="例如：革兰氏阳性菌感染" />
      </Form.Item>

      <Divider orientation="left">纳入标准</Divider>
      <Form.List name="inclusion_criteria">
        {(fields, { add, remove, move }) => (
          <>
            {fields.map((field, index) => (
              <div
                key={field.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  marginBottom: 8,
                  padding: '8px 12px',
                  background: '#fafafa',
                  borderRadius: 4,
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                  if (fromIndex !== index) {
                    move(fromIndex, index);
                  }
                }}
              >
                <div
                  style={{ cursor: 'move', paddingTop: 4, flexShrink: 0 }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', index.toString());
                  }}
                >
                  <HolderOutlined />
                </div>
                <Form.Item
                  {...field}
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[{ required: true, message: '请输入纳入标准' }]}
                >
                  <Input placeholder="例如：胎龄 ≥ 28 周" />
                </Form.Item>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(field.name)}
                  style={{ flexShrink: 0 }}
                />
              </div>
            ))}
            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
              添加纳入标准
            </Button>
          </>
        )}
      </Form.List>

      <Divider orientation="left">排除标准</Divider>
      <Form.List name="exclusion_criteria">
        {(fields, { add, remove, move }) => (
          <>
            {fields.map((field, index) => (
              <div
                key={field.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  marginBottom: 8,
                  padding: '8px 12px',
                  background: '#fafafa',
                  borderRadius: 4,
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                  if (fromIndex !== index) {
                    move(fromIndex, index);
                  }
                }}
              >
                <div
                  style={{ cursor: 'move', paddingTop: 4, flexShrink: 0 }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', index.toString());
                  }}
                >
                  <HolderOutlined />
                </div>
                <Form.Item
                  {...field}
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[{ required: true, message: '请输入排除标准' }]}
                >
                  <Input placeholder="例如：严重肝功能不全" />
                </Form.Item>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(field.name)}
                  style={{ flexShrink: 0 }}
                />
              </div>
            ))}
            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
              添加排除标准
            </Button>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default Step2DrugPopulation;
